/**
 * Portal Donations Page
 * Donation campaign management for PFONG (ONG) users
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { PermissionGate } from '../../components/PermissionGate';
import portalService from '../../services/portal.service';
import type { PortalCampaign, PortalDonation } from '../../types/portal.types';
import { fmtInstante } from '../../utils/tempoCv';

// Estado mappings
const ESTADO_LABELS: Record<string, { text: string; class: string }> = {
  A: { text: 'Ativa', class: 'bg-green-100 text-green-800' },
  ATIVA: { text: 'Ativa', class: 'bg-green-100 text-green-800' },
  P: { text: 'Pausada', class: 'bg-yellow-100 text-yellow-800' },
  PAUSADA: { text: 'Pausada', class: 'bg-yellow-100 text-yellow-800' },
  C: { text: 'Concluída', class: 'bg-blue-100 text-blue-800' },
  CONCLUIDA: { text: 'Concluída', class: 'bg-blue-100 text-blue-800' },
  X: { text: 'Cancelada', class: 'bg-red-100 text-red-800' },
  CANCELADA: { text: 'Cancelada', class: 'bg-red-100 text-red-800' },
  I: { text: 'Inactiva', class: 'bg-gray-100 text-gray-600' },
};

// Map frontend label values to DB values
const ESTADO_TO_DB: Record<string, string> = {
  ATIVA: 'A',
  PAUSADA: 'P',
  CONCLUIDA: 'C',
  CANCELADA: 'X',
  A: 'A',
  P: 'P',
  C: 'C',
  X: 'X',
};

const fmt = (val?: number | string | null) =>
  Number(val || 0).toLocaleString('pt-PT', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const isAtiva = (estado: string) => estado === 'ATIVA' || estado === 'A';

const getEstadoLabel = (estado: string) =>
  ESTADO_LABELS[estado] ?? { text: estado, class: 'bg-gray-100 text-gray-800' };

const formatDate = (val: any): string => {
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return fmtInstante(d, { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return String(val);
  }
};

interface CampaignFormData {
  TITULO: string;
  DESCRICAO: string;
  META: number;
  DATA_INICIO?: string;
  DATA_FIM?: string;
  CATEGORIA?: string;
  ESTADO: string; // stored as 'A'|'P'|'C'|'X'
}

const PortalDonations: React.FC = () => {
  const { hasPermission } = useAuth();
  const [campaigns, setCampaigns] = useState<PortalCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // CRUD modal
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<PortalCampaign | null>(null);
  const [formData, setFormData] = useState<CampaignFormData>({
    TITULO: '',
    DESCRICAO: '',
    META: 0,
    ESTADO: 'A',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<PortalCampaign | null>(null);
  const [donors, setDonors] = useState<PortalDonation[]>([]);
  const [donorsLoading, setDonorsLoading] = useState(false);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const res = await portalService.getCampaigns();
      setCampaigns(res.data || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar campanhas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadDonors = async (campaignId: string) => {
    try {
      setDonorsLoading(true);
      const res = await portalService.getCampaignDonors(campaignId);
      setDonors(res.data?.doadores || res.data || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar doadores');
    } finally {
      setDonorsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let imageBase64: string | undefined;
      let mimeType: string | undefined;
      if (imageFile) {
        mimeType = imageFile.type;
        imageBase64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve((reader.result as string).split(',')[1]);
          reader.readAsDataURL(imageFile);
        });
      }
      const payload: any = {
        titulo: formData.TITULO,
        descricao: formData.DESCRICAO,
        meta: formData.META,
        dataInicio: formData.DATA_INICIO || undefined,
        dataFim: formData.DATA_FIM || undefined,
        categoria: formData.CATEGORIA || undefined,
        estado: formData.ESTADO,
        ...(imageBase64 ? { imageBase64, mimeType } : {}),
      };
      if (editingCampaign) {
        await portalService.updateCampaign(editingCampaign.ID, payload);
      } else {
        await portalService.createCampaign(payload);
      }
      setShowModal(false);
      setEditingCampaign(null);
      setImageFile(null);
      setImagePreview(null);
      resetForm();
      loadCampaigns();
    } catch (err: any) {
      setError(err.message || 'Erro ao guardar campanha');
    }
  };

  const handleEdit = (campaign: PortalCampaign) => {
    setEditingCampaign(campaign);
    // Normalize DB estado to one of A|P|C|X
    const estadoDb = ESTADO_TO_DB[campaign.ESTADO] ?? campaign.ESTADO;
    setFormData({
      TITULO: campaign.TITULO,
      DESCRICAO: campaign.DESCRICAO || '',
      META: campaign.META ?? (campaign as any).META_VALOR ?? 0,
      DATA_INICIO: campaign.DATA_INICIO ? String(campaign.DATA_INICIO).split('T')[0] : undefined,
      DATA_FIM: campaign.DATA_FIM ? String(campaign.DATA_FIM).split('T')[0] : undefined,
      CATEGORIA: campaign.CATEGORIA,
      ESTADO: estadoDb,
    });
    setImageFile(null);
    setImagePreview(campaign.IMAGEM || null);
    setShowModal(true);
  };

  const handleViewDetail = async (campaign: PortalCampaign) => {
    setSelectedCampaign(campaign);
    setDonors([]);
    setShowDetailModal(true);
    await loadDonors(campaign.ID);
  };

  const resetForm = () => {
    setFormData({ TITULO: '', DESCRICAO: '', META: 0, ESTADO: 'A' });
  };

  const totalArrecadado = campaigns.reduce((sum, c) => sum + Number(c.totalArrecadado || 0), 0);
  const totalDoadores = campaigns.reduce((sum, c) => sum + Number(c.totalDoadores || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pagali-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campanhas de Donativos</h1>
          <p className="text-gray-600">Gerir campanhas de angariacao de fundos</p>
        </div>
        <PermissionGate permission="MY_DONATIONS_CREATE">
          <button
            onClick={() => {
              resetForm();
              setEditingCampaign(null);
              setShowModal(true);
            }}
            className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nova Campanha
          </button>
        </PermissionGate>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="text-sm text-red-500 underline mt-1">
            Fechar
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total Campanhas</p>
          <p className="text-2xl font-semibold">{campaigns.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Ativas</p>
          <p className="text-2xl font-semibold text-green-600">
            {campaigns.filter((c) => isAtiva(c.ESTADO)).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total Arrecadado</p>
          <p className="text-2xl font-semibold text-pink-600">
            {fmt(totalArrecadado)} ECV
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total Doadores</p>
          <p className="text-2xl font-semibold">{fmt(totalDoadores)}</p>
        </div>
      </div>

      {/* Campaigns Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {campaigns.length === 0 ? (
          <div className="col-span-full bg-white rounded-lg shadow p-12 text-center text-gray-500">
            Nenhuma campanha encontrada. Clique em "Nova Campanha" para criar.
          </div>
        ) : (
          campaigns.map((campaign) => {
            const estado = getEstadoLabel(campaign.ESTADO);
            const meta = Number(campaign.META ?? (campaign as any).META_VALOR ?? 0);
            const arrecadado = Number(campaign.totalArrecadado || 0);
            const percent = meta > 0 ? Math.min((arrecadado / meta) * 100, 100) : 0;
            return (
              <div key={campaign.ID} className="bg-white rounded-lg shadow overflow-hidden">
                {campaign.IMAGEM ? (
                  <img
                    src={campaign.IMAGEM.startsWith('http') ? campaign.IMAGEM : `data:${campaign.MIME_TYPE};base64,${campaign.IMAGEM}`}
                    alt={campaign.TITULO}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
                    <svg className="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">{campaign.TITULO}</h3>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${estado.class}`}>
                      {estado.text}
                    </span>
                  </div>
                  {campaign.DESCRICAO && (
                    <p className="mt-2 text-sm text-gray-600 line-clamp-2">{campaign.DESCRICAO}</p>
                  )}

                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-pink-600">
                        {fmt(arrecadado)} ECV
                      </span>
                      <span className="text-gray-500">
                        de {fmt(meta)} ECV
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-pink-500 to-rose-600 h-2 rounded-full transition-all"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{percent < 1 ? percent.toFixed(2) : percent < 10 ? percent.toFixed(1) : percent.toFixed(0)}% alcançado</span>
                      <span>{fmt(campaign.totalDoadores)} doadores</span>
                    </div>
                  </div>

                  {campaign.CATEGORIA && (
                    <div className="mt-4">
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        {campaign.CATEGORIA}
                      </span>
                    </div>
                  )}
                </div>
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
                  <button
                    onClick={() => handleViewDetail(campaign)}
                    className="text-sm text-pagali-primary hover:text-pagali-primary/80 font-medium"
                  >
                    Ver Detalhes
                  </button>
                  <div className="flex gap-4">
                    <PermissionGate permission="MY_DONATIONS_EDIT">
                      <button
                        onClick={() => handleEdit(campaign)}
                        className="text-sm text-gray-600 hover:text-gray-900"
                      >
                        Editar
                      </button>
                    </PermissionGate>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Campaign CRUD Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {editingCampaign ? 'Editar Campanha' : 'Nova Campanha'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titulo *</label>
                <input
                  type="text"
                  required
                  value={formData.TITULO}
                  onChange={(e) => setFormData({ ...formData, TITULO: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descricao</label>
                <textarea
                  value={formData.DESCRICAO}
                  onChange={(e) => setFormData({ ...formData, DESCRICAO: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meta (CVE)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={formData.META}
                  onChange={(e) => setFormData({ ...formData, META: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Inicio</label>
                  <input
                    type="date"
                    value={formData.DATA_INICIO || ''}
                    onChange={(e) => setFormData({ ...formData, DATA_INICIO: e.target.value || undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
                  <input
                    type="date"
                    value={formData.DATA_FIM || ''}
                    onChange={(e) => setFormData({ ...formData, DATA_FIM: e.target.value || undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                  <select
                    value={formData.CATEGORIA || ''}
                    onChange={(e) => setFormData({ ...formData, CATEGORIA: e.target.value || undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  >
                    <option value="">Selecionar...</option>
                    <option value="Saude">Saude</option>
                    <option value="Educacao">Educacao</option>
                    <option value="Ambiente">Ambiente</option>
                    <option value="Social">Social</option>
                    <option value="Cultura">Cultura</option>
                    <option value="Desporto">Desporto</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    value={formData.ESTADO}
                    onChange={(e) => setFormData({ ...formData, ESTADO: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  >
                    <option value="A">Ativa</option>
                    <option value="P">Pausada</option>
                    <option value="C">Concluida</option>
                    <option value="X">Cancelada</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Imagem da Campanha</label>
                {imagePreview && (
                  <img
                    src={imagePreview.startsWith('http') ? imagePreview : `data:image/jpeg;base64,${imagePreview}`}
                    alt="Preview"
                    className="w-full h-32 object-cover rounded-lg mb-2"
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setImageFile(file);
                      const reader = new FileReader();
                      reader.onload = () => setImagePreview(reader.result as string);
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-pink-50 file:text-pink-700 hover:file:bg-pink-100 cursor-pointer"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80"
                >
                  {editingCampaign ? 'Guardar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Campaign Detail Modal */}
      {showDetailModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{selectedCampaign.TITULO}</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {/* Campaign Info */}
              {selectedCampaign.IMAGEM && (
                <img
                  src={selectedCampaign.IMAGEM.startsWith('http') ? selectedCampaign.IMAGEM : `data:${selectedCampaign.MIME_TYPE};base64,${selectedCampaign.IMAGEM}`}
                  alt={selectedCampaign.TITULO}
                  className="w-full h-48 object-cover"
                />
              )}

              <div className="p-6 space-y-4">
                {selectedCampaign.DESCRICAO && (
                  <p className="text-sm text-gray-600">{selectedCampaign.DESCRICAO}</p>
                )}

                {/* Progress */}
                {(() => {
                  const meta = Number(selectedCampaign.META ?? (selectedCampaign as any).META_VALOR ?? 0);
                  const arrecadado = Number(selectedCampaign.totalArrecadado || 0);
                  const percent = meta > 0 ? Math.min((arrecadado / meta) * 100, 100) : 0;
                  return (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Meta</span>
                        <span className="font-semibold">{fmt(meta)} ECV</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Total Arrecadado</span>
                        <span className="font-semibold text-pink-600">{fmt(arrecadado)} ECV</span>
                      </div>
                      {meta > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Valor Restante</span>
                          <span className="font-semibold text-orange-500">{fmt(Math.max(0, meta - arrecadado))} ECV</span>
                        </div>
                      )}
                      <div className="w-full bg-gray-200 rounded-full h-3 mt-2">
                        <div
                          className="bg-gradient-to-r from-pink-500 to-rose-600 h-3 rounded-full transition-all"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 text-right">{percent < 1 ? percent.toFixed(2) : percent < 10 ? percent.toFixed(1) : percent.toFixed(0)}% da meta</p>
                    </div>
                  );
                })()}

                {/* Donors Section */}
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-3">
                    Doadores ({donorsLoading ? '...' : donors.length})
                  </h3>
                  {donorsLoading ? (
                    <div className="flex justify-center py-6">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pagali-primary"></div>
                    </div>
                  ) : donors.length === 0 ? (
                    <p className="text-center text-gray-500 py-6 text-sm">Nenhum donativo registado ainda.</p>
                  ) : (
                    <div className="space-y-2">
                      {donors.map((donor) => (
                        <div key={donor.ID} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                              {donor.ANONIMO ? (
                                <svg className="w-4 h-4 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              ) : (
                                <span className="text-pink-600 font-semibold text-sm">
                                  {((donor as any).DOADOR_NOME_REAL || donor.DOADOR_NOME || '?').charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-medium">
                                {donor.ANONIMO ? 'Anónimo' : ((donor as any).DOADOR_NOME_REAL || donor.DOADOR_NOME || 'Anónimo')}
                              </p>
                              <p className="text-xs text-gray-400">
                                {formatDate(donor.DATA_DONATIVO)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-pink-600">
                              {Number(donor.VALOR || 0).toLocaleString('pt-CV')} ECV
                            </p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              donor.ESTADO === 'CONFIRMADO' ? 'bg-green-100 text-green-800' :
                              donor.ESTADO === 'PENDENTE' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {donor.ESTADO}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalDonations;
